import React, { useState, useRef } from 'react';
import { UploadCloud, File, X, Loader2 } from 'lucide-react';
import { LoanDocument } from '../types';
import { DocumentItem } from './DocumentItem';
interface UploadedFile {
  id: string;
  file: File;
  status: 'uploading' | 'done';
}
interface DocumentListProps {
  documents: LoanDocument[];
  onFilesUploaded: (files: File[]) => void;
  onToggleDocument: (id: string) => void;
  onSelectAll: () => void;
}
export function DocumentList({
  documents,
  onFilesUploaded,
  onToggleDocument,
  onSelectAll
}: DocumentListProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const completedCount = documents.length - pendingCount;
  const progress = Math.round(completedCount / documents.length * 100);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(Array.from(e.dataTransfer.files));
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(Array.from(e.target.files));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const processFiles = (files: File[]) => {
    const newFiles: UploadedFile[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      status: 'uploading' as const
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    // Simulate upload delay then mark done
    setTimeout(() => {
      setUploadedFiles(prev => prev.map(f => newFiles.find(nf => nf.id === f.id) ? {
        ...f,
        status: 'done' as const
      } : f));
      onFilesUploaded(files);
    }, 1500);
  };
  const removeFile = (id: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };
  return <div className="space-y-6" data-id="element-17">
      {/* Document Checklist */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-18">
        <div className="p-6 border-b border-slate-100" data-id="element-19">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-20">
            <div data-id="element-21">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-22">
                Required Documents
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-23">
                Please provide the following documents to process your
                application.
              </p>
            </div>
            <div className="text-right" data-id="element-24">
              <div className="text-2xl font-bold text-slate-900" data-id="element-25">
                {progress}%
              </div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-26">
                Completed
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-100 rounded-full h-2.5 mb-2 overflow-hidden" data-id="element-27">
            <div className="bg-green-600 h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
            width: `${progress}%`
          }} data-id="element-28"></div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-29">
            {completedCount} of {documents.length} documents provided
          </p>
        </div>

        <div className="p-4 sm:p-6" data-id="element-30">
          <div className="flex items-center justify-between mb-3 px-4" data-id="element-31">
            <button onClick={onSelectAll} className="text-sm font-medium text-green-600 hover:text-green-700 transition-colors" data-id="element-32">
              {documents.every(d => d.status !== 'pending') ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-xs text-slate-400" data-id="element-33">
              {documents.filter(d => d.status !== 'pending').length} selected
            </span>
          </div>
          <div className="space-y-1" data-id="element-34">
            {documents.map(doc => <DocumentItem key={doc.id} document={doc} onToggle={onToggleDocument} data-id="element-35" />)}
          </div>
        </div>
      </div>

      {/* Unified Upload Zone */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-36">
        <div className="p-6 border-b border-slate-100" data-id="element-37">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-38">
            Upload Documents
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-39">
            Drop all your files here — we'll sort them out for you.
          </p>
        </div>

        <div className="p-6" data-id="element-40">
          <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
              ${isDragging ? 'border-green-500 bg-green-50 scale-[1.01]' : 'border-gray-200 hover:border-green-300 hover:bg-green-50/30'}
            `} data-id="element-41">
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple data-id="element-42" />
            <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-43">
              <UploadCloud className="w-7 h-7" data-id="element-44" />
            </div>
            <p className="text-sm font-medium text-slate-900 mb-1" data-id="element-45">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-slate-500" data-id="element-46">
              PDF, JPG, PNG, or DOC — upload multiple files at once
            </p>
          </div>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && <div className="mt-4 space-y-2" data-id="element-47">
              {uploadedFiles.map(uf => <div key={uf.id} className="flex items-center bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-48">
                  <div className="w-9 h-9 bg-white rounded-lg border border-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-49">
                    <File className="w-4 h-4 text-green-600" data-id="element-50" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-51">
                    <p className="text-sm font-medium text-slate-900 truncate" data-id="element-52">
                      {uf.file.name}
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-53">
                      {(uf.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  {uf.status === 'uploading' ? <Loader2 className="w-4 h-4 text-green-600 animate-spin ml-3 flex-shrink-0" data-id="element-54" /> : <button onClick={e => {
              e.stopPropagation();
              removeFile(uf.id);
            }} className="ml-3 p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors flex-shrink-0" data-id="element-55">
                      <X className="w-4 h-4" data-id="element-56" />
                    </button>}
                </div>)}
            </div>}
        </div>
      </div>
    </div>;
}